import { PricingUtilitiesError } from './errorMessages';

export default class PricingModelCrudUtilities {

  constructor(firebase, db) {
    this.firebase = firebase
    this.db = db
  }

  getPricingModelTemplate() {
    return {
      name: '', 
      prices: [{
        effectiveDate: new Date().toLocaleDateString('en-US'),
        city: '',
        materials: [{
          material: [''],
          loads: [0]
        }]
      }]
    }
  }

  validatePricingModel = (pricingModel) => {
    // Validate name
    if (!pricingModel.name || !pricingModel.name.trim()) {
      throw new Error(PricingUtilitiesError.pricingModelMustHaveName)
    }

    // Validate has at least one price
    if (!pricingModel.prices || pricingModel.prices.length === 0) {
      throw new Error(PricingUtilitiesError.pricingModelMustHaveOnePrice)
    }

    // Validate each price
    pricingModel.prices.forEach((price, priceIndex) => {
      // Validate effective date
      if (!price.effectiveDate) {
        throw new Error(PricingUtilitiesError.effectiveDateRequired)
      }

      // Validate date format (basic check)
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
      if (!dateRegex.test(price.effectiveDate)) {
        throw new Error(PricingUtilitiesError.invalidDateFormat)
      }

      // Validate materials
      if (!price.materials || price.materials.length === 0) {
        throw new Error(`Price entry ${priceIndex + 1} must have at least one material group`)
      }

      price.materials.forEach((materialGroup, materialIndex) => {
        // Validate material names
        if (!materialGroup.material || materialGroup.material.length === 0 || 
            materialGroup.material.every(m => !m.trim())) {
          throw new Error(PricingUtilitiesError.materialNameRequired)
        }

        // Validate loads
        if (!materialGroup.loads || materialGroup.loads.length === 0) {
          throw new Error(`Material group ${materialIndex + 1} in price ${priceIndex + 1} must have at least one load price`)
        }

        materialGroup.loads.forEach((load, loadIndex) => {
          // Validate numeric
          if (typeof load !== 'number' || isNaN(load)) {
            throw new Error(PricingUtilitiesError.loadPriceMustBeNumeric)
          }

          // Validate positive
          if (load < 0) {
            throw new Error(PricingUtilitiesError.loadPriceMustBePositive)
          }
        })

        // Validate two-load pricing rule
        if (materialGroup.loads.length === 2) {
          const [singleLoad, doubleLoadPerItem] = materialGroup.loads
          if (doubleLoadPerItem >= singleLoad) {
            throw new Error(PricingUtilitiesError.secondLoadPriceMustBeLess)
          }
        }
      })
    })
  }

  addPricingModel = async (pricingModel) => {
    let newModel = {...this.getPricingModelTemplate(), ...pricingModel}
    
    // Validate the model
    this.validatePricingModel(newModel)
    
    // Check for duplicate name
    const existingModel = await this.getByName(newModel.name)
    if (existingModel) {
      throw new Error(PricingUtilitiesError.duplicatePricingModelName)
    }

    // Add to Firestore
    let modelRef = this.db.collection('pricing').doc()
    await modelRef.set(newModel)
    return modelRef.id
  }

  updatePricingModelByName = async (name, pricingModel) => {
    // Validate the model
    this.validatePricingModel(pricingModel)
    
    // Find the model by name
    const querySnapshot = await this.db.collection('pricing')
      .where('name', '==', name)
      .get()
    
    if (querySnapshot.empty) {
      throw new Error('Pricing model not found')
    }

    // Check for duplicate name if name is being changed
    if (pricingModel.name !== name) {
      const existingModel = await this.getByName(pricingModel.name)
      if (existingModel) {
        throw new Error(PricingUtilitiesError.duplicatePricingModelName)
      }
    }

    // Update the first (should be only) matching document
    const docRef = querySnapshot.docs[0].ref
    await docRef.set(pricingModel)
  }

  deletePricingModelByName = async (name) => {
    const querySnapshot = await this.db.collection('pricing')
      .where('name', '==', name)
      .get()
    
    if (querySnapshot.empty) {
      throw new Error('Pricing model not found')
    }

    // Delete all matching documents (should be only one)
    const batch = this.db.batch()
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref)
    })
    await batch.commit()
  }

  getByName = async (name) => {
    const querySnapshot = await this.db.collection('pricing')
      .where('name', '==', name)
      .get()
    
    if (querySnapshot.empty) {
      return null
    }

    const doc = querySnapshot.docs[0]
    return { id: doc.id, ...doc.data() }
  }

  getAllPricingModels = async () => {
    const querySnapshot = await this.db.collection('pricing').get()
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data()
    }))
  }
}